import React from 'react';
import PropTypes from 'prop-types';
import { filePresenceValidation } from 'platform/forms-system/src/js/validation';

import ArtifactSummary from './ArtifactSummaryReview';

/**
 * Factory that builds a `CustomPageReview` component for the CAVE upload page.
 *
 * On the review-and-submit page it replaces the default upload-field review
 * with:
 *   1. a read-only list of the uploaded files,
 *   2. a "Change files" button that returns the user to the uploader (so any
 *      change re-runs conflict resolution), and
 *   3. the CAVE-extracted document data (DD-214 / death certificate), editable
 *      inline beneath the files.
 *
 * Keeping this in the CAVE module means consumers only need to wire the
 * returned component as the page's `CustomPageReview`.
 *
 * @param {object} [options]
 * @param {string} [options.filesKey='files'] - form-data key holding the files
 * @param {string} [options.uploadPath] - path of the in-flow upload page
 * @param {string} [options.title] - optional heading for the review section
 * @returns {Function} a CustomPageReview component
 */
export const createCaveUploadReview = ({
  filesKey = 'files',
  uploadPath = '/additional-information/upload-documents-cave',
  title,
} = {}) => {
  function CaveUploadReview({ data, goToPath, title: propTitle }) {
    const heading = title ?? propTitle;
    const allFiles = data?.[filesKey] ?? [];
    const files = allFiles.filter(filePresenceValidation);

    return (
      <div className="form-review-panel-page">
        {heading && (
          <div className="form-review-panel-page-header-row">
            <h4 className="form-review-panel-page-header vads-u-font-size--h5 vads-u-margin--0">
              {heading}
            </h4>
          </div>
        )}

        <dl className="review">
          {files.length === 0 ? (
            <div className="review-row">
              <dt>Uploaded files</dt>
              <dd>No files uploaded</dd>
            </div>
          ) : (
            files.map((file, index) => (
              <div className="review-row" key={index}>
                <dt>File name</dt>
                <dd className="dd-privacy-mask">
                  {file.name || 'No file name'}
                </dd>
              </div>
            ))
          )}
        </dl>

        <va-button
          secondary
          uswds
          text="Change files"
          onClick={() => goToPath(uploadPath)}
          class="vads-u-margin-bottom--3"
          data-dd-action-name="Change Files"
        />

        <ArtifactSummary />
      </div>
    );
  }

  CaveUploadReview.propTypes = {
    data: PropTypes.object,
    goToPath: PropTypes.func,
    title: PropTypes.string,
  };

  return CaveUploadReview;
};

export default createCaveUploadReview;
